import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { searchRequest } from 'core/services/product';
import { listRequest, reportRequest } from 'core/services/admin';
import { EntityTypes } from 'constants/entities';
import { LoadingState } from 'types/domain/base';
import { ISearch, ISearchResponse, SearchType, SortBy } from 'types/domain/search';

/**
 * The two requests behind the top-bar search. They are ordinary
 * `searchRequest`/`listRequest` calls tagged with a componentId so their
 * results land in `state.global` instead of the legacy page-scoped fields —
 * typing in the top bar must not disturb whatever a page has in its own
 * search box.
 */
/** Below this a term matches most of the organisation, so nothing is requested. */
export const MIN_GLOBAL_SEARCH_TERM = 2;
export const GLOBAL_SEARCH_TEXT_ID = 'global-search-text';
export const GLOBAL_SEARCH_LEARNER_ID = 'global-search-learners';

/** Ids only: the rows themselves are merged into the entity store. */
export interface IGlobalSearch {
  term: string;
  /** TEXT ids from /api/admin/search — module titles and action copy. */
  textIds: number[];
  /** ORG_USER ids from /api/admin/list. */
  orgUserIds: number[];
  /** One flag per request — the bar is only idle once both have settled. */
  textLoading: boolean;
  learnerLoading: boolean;
}

export interface IComponentPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  loading: boolean;
  ids: number[]; // Store IDs returned from server for this page
  searchTerm?: string; // Component-specific search term
}

export interface ISearchReducer {
  term: string; // Deprecated: kept for backwards compatibility
  filter: number;
  sort: SortBy | number;
  response: ISearchResponse;
  loading: LoadingState;
  searchType: SearchType;
  selected: ISearchSelection[];
  components: Record<string, IComponentPagination>;
  /**
   * Load state of the plain, un-scoped `listRequest` per entity type — the
   * shell's course index, a page's module list. Component-scoped lists keep
   * their own flag on `components`; these have nowhere else to record that a
   * fetch is still in flight, which is what a page needs to tell "no rows yet"
   * from "no rows at all".
   */
  lists: Partial<Record<EntityTypes, LoadingState>>;
  global: IGlobalSearch;
}

export interface ISearchSelection {
  moduleId: number;
  actionId: number;
  index: number;
  created: number;
}

const initialState: ISearchReducer = {
  term: null,
  filter: null,
  sort: SortBy.MODIFIED,
  response: null,
  loading: LoadingState.DEFAULT,
  searchType: null,
  selected: [],
  components: {},
  lists: {},
  global: {
    term: '',
    textIds: [],
    orgUserIds: [],
    textLoading: false,
    learnerLoading: false,
  },
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    updateSearch(state, action: PayloadAction<ISearch>) {
      // Update global search state for backwards compatibility
      const { term, componentId, ...rest } = action.payload;

      // If componentId is provided, update component-specific search term
      if (componentId) {
        if (!state.components[componentId]) {
          state.components[componentId] = {
            currentPage: 0,
            pageSize: 10,
            totalRecords: 0,
            loading: true,
            ids: [],
            searchTerm: term || '',
          };
        } else {
          state.components[componentId].searchTerm = term || '';
          state.components[componentId].loading = true;
        }
      }

      // Also update global state for backwards compatibility (mutate, don't return)
      state.term = term;
      state.response = null;
      Object.assign(state, rest);
    },

    /** Top-bar search term. Deliberately separate from `state.term`. */
    updateGlobalSearch(state, action: PayloadAction<string>) {
      // Trimmed, because this is what the requests are keyed on: a response is
      // matched back to the term that asked for it before it is applied.
      const term = (action.payload || '').trim();
      state.global.term = term;

      if (term.length < MIN_GLOBAL_SEARCH_TERM) {
        state.global.textIds = [];
        state.global.orgUserIds = [];
        state.global.textLoading = false;
        state.global.learnerLoading = false;
      } else {
        // Set here rather than on `pending` so the dropdown reads as searching
        // through the debounce, not only once the requests are in flight.
        state.global.textLoading = true;
        state.global.learnerLoading = true;
      }
    },
    clearGlobalSearch(state) {
      state.global = { term: '', textIds: [], orgUserIds: [], textLoading: false, learnerLoading: false };
    },

    selectSearchResult(state, action: PayloadAction<ISearchSelection>) {
      state.selected.push(action.payload);
    },
    clearSearchLoading(state, action: PayloadAction<{ componentId: string }>) {
      const { componentId } = action.payload;
      if (componentId && state.components[componentId]) {
        state.components[componentId].loading = false;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchRequest.pending, (state, action: any) => {
        if (action.meta.arg.componentId === GLOBAL_SEARCH_TEXT_ID) {
          state.global.textLoading = true;
          return;
        }

        state.loading = LoadingState.LOADING;
        // Handle componentId for pagination if provided
        const { componentId, pagerInfo, term } = action.meta.arg;
        if (componentId) {
          if (!state.components[componentId]) {
            state.components[componentId] = {
              currentPage: pagerInfo?.page || 0,
              pageSize: pagerInfo?.pageCount || 10,
              totalRecords: 0,
              loading: true,
              ids: [],
              searchTerm: term || '',
            };
          } else {
            state.components[componentId].loading = true;
            state.components[componentId].searchTerm = term || '';
          }
        }
      })
      .addCase(searchRequest.rejected, (state, action: any) => {
        if (action.meta.arg.componentId === GLOBAL_SEARCH_TEXT_ID) {
          state.global.textIds = [];
          state.global.textLoading = false;
          return;
        }

        state.loading = LoadingState.ERROR;
        // Handle componentId for pagination if provided
        const { componentId } = action.meta.arg;
        if (componentId && state.components[componentId]) {
          state.components[componentId].loading = false;
          state.components[componentId].ids = [];
        }
      })
      .addCase(searchRequest.fulfilled, (state, action: any) => {
        // /api/learner/search wraps the response in an auth payload, /api/admin/search
        // returns it bare. Both reach this case.
        const searchResponse = action.payload?.searchResponse ?? action.payload;
        const { componentId, pagerInfo, term } = action.meta.arg;

        if (componentId === GLOBAL_SEARCH_TEXT_ID) {
          // A slower earlier keystroke must not overwrite the current term's hits.
          if ((term || '') !== state.global.term) return;

          state.global.textIds = searchResponse?.results?.list?.map((item: any) => item.id) ?? [];
          state.global.textLoading = false;
          return;
        }

        state.loading = LoadingState.LOADED;
        state.response = searchResponse;
        // Handle componentId for pagination if provided
        if (componentId && searchResponse?.results) {
          // Extract IDs from the search results list
          const ids = searchResponse.results.list ? searchResponse.results.list.map((item: any) => item.id) : [];
          state.components[componentId] = {
            currentPage: pagerInfo?.page || 0,
            pageSize: pagerInfo?.pageCount || 10,
            totalRecords: searchResponse.results.pagerInfo?.totalRecords || ids.length,
            loading: false,
            ids,
            searchTerm: term || '',
          };
        }
      })
      .addCase(listRequest.pending, (state, action: any) => {
        const { componentId, pagerInfo, search, type } = action.meta.arg;
        if (!componentId && type) state.lists[type] = LoadingState.LOADING;
        if (componentId === GLOBAL_SEARCH_LEARNER_ID) {
          state.global.learnerLoading = true;
          return;
        }
        if (componentId) {
          if (!state.components[componentId]) {
            state.components[componentId] = {
              currentPage: pagerInfo?.page || 0,
              pageSize: pagerInfo?.pageCount || 10,
              totalRecords: 0,
              loading: true,
              ids: [],
              searchTerm: search || '',
            };
          } else {
            state.components[componentId].loading = true;
            state.components[componentId].currentPage = pagerInfo?.page || state.components[componentId].currentPage;
            state.components[componentId].pageSize = pagerInfo?.pageCount || state.components[componentId].pageSize;
            state.components[componentId].searchTerm = search || '';
          }
        }
      })
      .addCase(listRequest.fulfilled, (state, action: any) => {
        const { componentId, pagerInfo, search, type } = action.meta.arg;
        if (!componentId && type) state.lists[type] = LoadingState.LOADED;
        if (componentId === GLOBAL_SEARCH_LEARNER_ID) {
          if ((action.meta.arg.filter?.search || '') !== state.global.term) return;

          state.global.orgUserIds = action.payload.list ? action.payload.list.map((item: any) => item.id) : [];
          state.global.learnerLoading = false;
          return;
        }
        if (componentId) {
          // Extract IDs from the returned list
          const ids = action.payload.list ? action.payload.list.map((item: any) => item.id) : [];
          state.components[componentId] = {
            currentPage: pagerInfo.page,
            pageSize: pagerInfo.pageCount || 10,
            totalRecords: action.payload.pagerInfo?.totalRecords || 0,
            loading: false,
            ids,
            searchTerm: search || '',
          };
        }
      })
      .addCase(listRequest.rejected, (state, action: any) => {
        const { componentId, type } = action.meta.arg;
        if (!componentId && type) state.lists[type] = LoadingState.ERROR;
        if (componentId === GLOBAL_SEARCH_LEARNER_ID) {
          state.global.orgUserIds = [];
          state.global.learnerLoading = false;
          return;
        }
        if (componentId && state.components[componentId]) {
          state.components[componentId].loading = false;
          state.components[componentId].ids = [];
        }
      })
      // Add listeners for reportRequest (same pattern as listRequest)
      .addCase(reportRequest.pending, (state, action: any) => {
        const { componentId, pagerInfo, search } = action.meta.arg;
        if (componentId) {
          if (!state.components[componentId]) {
            state.components[componentId] = {
              currentPage: pagerInfo?.page || 0,
              pageSize: pagerInfo?.pageCount || 10,
              totalRecords: 0,
              loading: true,
              ids: [],
              searchTerm: search || '',
            };
          } else {
            state.components[componentId].loading = true;
            state.components[componentId].currentPage = pagerInfo?.page || state.components[componentId].currentPage;
            state.components[componentId].pageSize = pagerInfo?.pageCount || state.components[componentId].pageSize;
            state.components[componentId].searchTerm = search || '';
          }
        }
      })
      .addCase(reportRequest.fulfilled, (state, action: any) => {
        const { componentId, pagerInfo, search } = action.meta.arg;

        if (componentId) {
          // Extract IDs from the returned list
          const ids = action.payload.list ? action.payload.list.map((item: any) => item.id) : [];

          state.components[componentId] = {
            currentPage: pagerInfo.page,
            pageSize: pagerInfo.pageCount || 10,
            totalRecords: action.payload.pagerInfo?.totalRecords || 0,
            loading: false,
            ids,
            searchTerm: search || '',
          };
        }
      })
      .addCase(reportRequest.rejected, (state, action: any) => {
        const { componentId } = action.meta.arg;
        if (componentId && state.components[componentId]) {
          state.components[componentId].loading = false;
          state.components[componentId].ids = [];
        }
      });
  },
});

export const { updateSearch, selectSearchResult, clearSearchLoading, updateGlobalSearch, clearGlobalSearch } =
  searchSlice.actions;

// Selector to get component pagination state
export const selectComponentPagination = (componentId: string) => (state: any) =>
  state.core.search.components[componentId] || {
    currentPage: 0,
    pageSize: 10,
    totalRecords: 0,
    loading: false,
    ids: [],
    searchTerm: '',
  };

export default searchSlice.reducer;
